import type { KpiDimStackableRow } from '~/api/endpoints/catalog'

/** Chart-semantics doctrine (spec 2026-08-03 §1): no surface renders a cross-series total —
 *  stacked geometry, Σ, or percentage-of-total — unless this resolver has blessed the
 *  selection. ONE precedence order, consulted by every consumer (knobs, Σ row,
 *  Show-Percentage, pin replay, Dynamics). */

export type AdditivityReason = 'ok' | 'vt-heterogeneous' | 'dim-rule' | 'metadata-pending'

export interface AdditivityVerdict {
  additive: boolean
  reason: AdditivityReason
}

export const ADDITIVE_OK: AdditivityVerdict = Object.freeze({ additive: true, reason: 'ok' })

export interface AdditivityInput {
  groupBy: string[]
  /** DISTINCT value_types in the ARRIVED rows AFTER the VT filter (resolveEffectiveVts). */
  effectiveValueTypes: string[]
  /** useKpiDimStackable data; undefined while loading. */
  stackableRows: KpiDimStackableRow[] | undefined
  kpi: string
}

export function resolveSeriesAdditivity(input: AdditivityInput): AdditivityVerdict {
  // 1. VT heterogeneity FIRST, regardless of group-by: dim×VT stacking sums across both
  //    axes and the dim guard vouches only for the dim. THE RELAXATION SEAM: a future
  //    data-side VT-additivity signal plugs in exactly here and nowhere else (spec §4.1).
  if (input.effectiveValueTypes.length > 1) {
    return { additive: false, reason: 'vt-heterogeneous' }
  }
  // 2. Group-by present → the v_kpi_dim_stackable verdict; pending-deny while loading
  //    (replaces the old flash-of-allowed). Multi-dim group-by ANDs the per-dim rows.
  //    Absent row defaults ALLOW — the existing dim-rule semantics, unchanged.
  if (input.groupBy.length > 0) {
    if (input.stackableRows === undefined) {
      return { additive: false, reason: 'metadata-pending' }
    }
    const rows = input.stackableRows
    const allStackable = input.groupBy.every(
      (dim) => rows.find((r) => r.kpi === input.kpi && r.dim_name === dim)?.is_stackable ?? true,
    )
    return allStackable ? ADDITIVE_OK : { additive: false, reason: 'dim-rule' }
  }
  // 3. No group-by, single VT: nothing to vouch for — additive, even while metadata loads.
  return ADDITIVE_OK
}

/** Distinct non-null value_types of a row set, sorted (deterministic across renders). */
export function distinctValueTypes(rows: ReadonlyArray<{ value_type?: string | null }>): string[] {
  const set = new Set<string>()
  for (const r of rows) if (r.value_type != null) set.add(r.value_type)
  return [...set].sort()
}
